import {
  LANG_CACHE_NAME,
  LEGACY_MEDIA_CACHE_NAMES_SW,
  MEDIA_CACHE_AVATARS_MAX_BYTES,
  MEDIA_CACHE_LAST_ACCESS_HEADER,
  MEDIA_CACHE_MAX_BYTES_TOTAL,
  MEDIA_CACHE_NAME,
  MEDIA_CACHE_NAME_AVATARS,
  MEDIA_CACHE_NAME_SW,
  MEDIA_CACHE_SIZE_HEADER,
  MEDIA_FRAME_CACHE_NAME,
  MEDIA_PROGRESSIVE_CACHE_MAX_BYTES,
  MEDIA_PROGRESSIVE_CACHE_NAME,
} from '../config';
import { yieldToMain } from './browser/scheduler';
import { IS_FIREFOX } from './browser/windowEnvironment';
import { clearLoadedStaticProxyUrls } from './mediaProxyUrl';
import { ACCOUNT_SLOT } from './multiaccount';

const cacheApi = self.caches;

const LAST_ACCESS_HEADER = MEDIA_CACHE_LAST_ACCESS_HEADER;
// Written at save time so the cleanup pass can total a cache up from headers alone. Sizing entries by
// reading their bodies back would mean pulling the whole cache (~1 GB) off disk every hour.
const SIZE_HEADER = MEDIA_CACHE_SIZE_HEADER;
const CACHE_TTL = 5 * 24 * 60 * 60 * 1000; // 5 days
const ACCESS_THROTTLE = 24 * 60 * 60 * 1000; // 1 day
const CLEANUP_INTERVAL = 1 * 60 * 60 * 1000; // 1 hour
const DORMANT_CLEANUP_INTERVAL = 24 * 60 * 60 * 1000; // 1 day
// Far enough past cold start that the dormant-slot pass is not competing with state hydration and the
// first render, close enough that a session shorter than `DORMANT_CLEANUP_INTERVAL` still runs one
const DORMANT_CLEANUP_BOOT_DELAY = 5 * 60 * 1000; // 5 minutes
// Entries to walk between yields. `yieldToMain` falls back to `setTimeout(0)` wherever
// `scheduler.yield` is missing — Safari, and the Tauri build with it — where the 4 ms clamp made a
// yield-per-entry pass cost 4 ms x every entry in the cache before any of the actual work. Yielding
// per batch still keeps the pass off the thread without paying that round trip thousands of times.
const CLEANUP_YIELD_BATCH = 25;
const QUOTA_SHED_COOLDOWN = 5 * 60 * 1000; // 5 minutes
const PERSISTENCE_DENIED_KEY = 'tt-storage-persist-denied';
// How many legacy entries (saved before `SIZE_HEADER` existed) to measure per pass to estimate the
// size of the rest. They are only replaced when their media is re-fetched, so a bounded sample keeps
// the budget honest without a full read.
const SIZE_SAMPLE_LIMIT = 20;

const CLEARABLE_CACHE_NAMES = [MEDIA_CACHE_NAME, MEDIA_CACHE_NAME_AVATARS, MEDIA_PROGRESSIVE_CACHE_NAME];

const MAX_BYTES_BY_CACHE_NAME: Record<string, number> = {
  [MEDIA_CACHE_NAME]: MEDIA_CACHE_MAX_BYTES_TOTAL,
  [MEDIA_CACHE_NAME_AVATARS]: MEDIA_CACHE_AVATARS_MAX_BYTES,
  [MEDIA_PROGRESSIVE_CACHE_NAME]: MEDIA_PROGRESSIVE_CACHE_MAX_BYTES,
};

// Boot pass stays cheap — this tab's own slot only. Sweeping every slot means enumerating and reading
// N times as many entries, and it runs during cold start, in every tab, competing with state hydration
// and the first render.
void cleanupCurrentSlot();
// Only the open slot gains entries, so only it earns an hourly pass. A dormant slot's media merely
// ages, while walking it costs a `cache.match` per entry — on a profile with several slots the hourly
// tick became thousands of Cache Storage reads for caches that had not changed since the last one.
// Age those daily instead; `shedLoadForQuota` still sweeps every slot the moment quota actually bites.
setInterval(() => {
  void cleanupCurrentSlot();
}, CLEANUP_INTERVAL);
setTimeout(() => {
  void cleanupAllSlots();
}, DORMANT_CLEANUP_BOOT_DELAY);
setInterval(() => {
  void cleanupAllSlots();
}, DORMANT_CLEANUP_INTERVAL);

type CacheEntry = {
  request: Request;
  lastAccess: number;
  size: number;
};

let isSupported: boolean | undefined;

export async function isCacheApiSupported() {
  if (!cacheApi) return false;

  isSupported = isSupported ?? await cacheApi.has('test').then(() => true).catch(() => false);
  return isSupported;
}

export enum Type {
  Text,
  Blob,
  Json,
  ArrayBuffer,
}

function getCacheName(cacheName: string) {
  if (cacheName === LANG_CACHE_NAME) return cacheName;

  const suffix = ACCOUNT_SLOT ? `_${ACCOUNT_SLOT}` : '';
  return `${cacheName}${suffix}`;
}

export async function fetch(
  cacheName: string, key: string, type: Type, isHtmlAllowed = false,
) {
  if (!cacheApi) {
    return undefined;
  }

  try {
    // To avoid the error "Request scheme 'webdocument' is unsupported"
    const request = new Request(key.replace(/:/g, '_'));
    const cache = await cacheApi.open(getCacheName(cacheName));
    const response = await cache.match(request);
    if (!response) {
      return undefined;
    }

    const lastAccess = Number(response.headers.get(LAST_ACCESS_HEADER));
    const now = Date.now();
    if (!lastAccess || now - lastAccess > ACCESS_THROTTLE) {
      updateAccessTime(cache, request, response);
    }

    const contentType = response.headers.get('Content-Type');

    switch (type) {
      case Type.Text:
        return await response.text();
      case Type.Blob: {
        // Ignore deprecated data-uri avatars
        if (key.startsWith('avatar') && contentType && contentType.startsWith('text')) {
          return undefined;
        }

        const blob = await response.blob();
        const shouldRecreate = !blob.type || (!isHtmlAllowed && blob.type.includes('html'));
        // iOS Safari fails to preserve `type` in cache
        let resolvedType = blob.type || contentType;

        if (!(shouldRecreate && resolvedType)) {
          return blob;
        }

        // Prevent HTML-in-video attacks (for files that were cached before fix)
        if (!isHtmlAllowed) {
          resolvedType = resolvedType.replace(/html/gi, '');
        }

        return new Blob([blob], { type: resolvedType });
      }
      case Type.Json:
        return await response.json();
      case Type.ArrayBuffer:
        return await response.arrayBuffer();
      default:
        return undefined;
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(err);
    return undefined;
  }
}

// Ask the browser to move this origin out of the "best-effort" bucket. Best-effort means the browser
// may evict the WHOLE origin when the disk fills — not just media, but `localStorage`, which holds the
// session, so the user is signed out of every account and loses all cached history in one step. The
// app self-imposes ~340 MB of media ceilings per account slot plus IndexedDB, so it is squarely in the
// size range where that happens. Chrome grants this silently to an installed/engaged origin; a denial
// costs nothing beyond the log, and every ceiling still applies.
export async function requestPersistentStorage() {
  if (!navigator.storage?.persist) return false;

  try {
    if (await navigator.storage.persisted()) return true;
    // Only Firefox puts this behind a permission prompt, so only there is a refusal worth
    // remembering — asking again every load would put a doorhanger in front of a user who said no.
    // Chrome and Safari decide silently from engagement, and that verdict changes over time: Safari
    // grants once the site is added to the Dock, Chrome once the origin is installed or engaged
    // enough. Remembering their "no" made the refusal at first run permanent, so the origin stayed
    // best-effort — evictable whole, session included — no matter what the user did afterwards.
    if (IS_FIREFOX && localStorage.getItem(PERSISTENCE_DENIED_KEY)) return false;

    const isGranted = await navigator.storage.persist();
    if (!isGranted && IS_FIREFOX) localStorage.setItem(PERSISTENCE_DENIED_KEY, '1');

    return isGranted;
  } catch {
    return false;
  }
}

// A quota-exceeded write means the browser is about to start evicting on its own terms. Shed load
// first, so what goes is the oldest media rather than whatever the browser picks (which can be the
// whole bucket). Guarded so a burst of failing writes queues one pass, not hundreds.
let isSheddingForQuota = false;
let lastShedAt = 0;

async function shedLoadForQuota() {
  // The overlap guard alone is not enough: media writes fail in bursts, so each finished pass would be
  // followed immediately by another, and a sweep that walks every slot's cache is not something to run
  // back to back. Once every few minutes is as often as it can usefully help.
  if (isSheddingForQuota || Date.now() - lastShedAt < QUOTA_SHED_COOLDOWN) return;
  isSheddingForQuota = true;
  lastShedAt = Date.now();

  try {
    await cleanupAllSlots();
  } finally {
    isSheddingForQuota = false;
  }
}

function isQuotaError(err: unknown) {
  return err instanceof DOMException
    && (err.name === 'QuotaExceededError' || err.name === 'NS_ERROR_DOM_QUOTA_REACHED');
}

export async function save(cacheName: string, key: string, data: AnyLiteral | Blob | ArrayBuffer | string) {
  if (!cacheApi) {
    return false;
  }

  try {
    const cacheData = typeof data === 'string' || data instanceof Blob || data instanceof ArrayBuffer
      ? data
      : JSON.stringify(data);
    // To avoid the error "Request scheme 'webdocument' is unsupported"
    const request = new Request(key.replace(/:/g, '_'));
    const response = new Response(cacheData);
    response.headers.set(LAST_ACCESS_HEADER, Date.now().toString());
    response.headers.set(SIZE_HEADER, getDataSize(cacheData).toString());
    const cache = await cacheApi.open(getCacheName(cacheName));
    await cache.put(request, response);

    return true;
  } catch (err) {
    if (isQuotaError(err)) {
      void shedLoadForQuota();
    }

    // eslint-disable-next-line no-console
    console.warn(err);
    return false;
  }
}

export async function remove(cacheName: string, key: string) {
  try {
    if (!cacheApi) {
      return undefined;
    }

    const cache = await cacheApi.open(getCacheName(cacheName));
    // Same key transform `save`/`fetch` apply, or this silently deletes nothing for any key holding a
    // colon — `staticMap:<hash>` location previews among them, which could then never be evicted.
    return await cache.delete(key.replace(/:/g, '_'));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(err);
    return undefined;
  }
}

export async function clear(cacheName: string) {
  try {
    if (!cacheApi) {
      return undefined;
    }

    return await cacheApi.delete(getCacheName(cacheName));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(err);
    return undefined;
  }
}

// Sweep every account slot's copy of the clearable caches, not just the one this tab happens to be
// signed into. `getCacheName` pins each name to `ACCOUNT_SLOT`, fixed at import from the tab's URL, so
// the TTL and the byte budget only ever reached the open slot: a second account used for a week and
// then left closed kept its media forever (five days of ONE active account measured 1.2 GB), and with
// six slots that is pure quota pressure pushing the origin toward wholesale eviction.
async function cleanupCurrentSlot() {
  if (!cacheApi) return;

  try {
    for (const cacheName of CLEARABLE_CACHE_NAMES) {
      await cleanupCache(getCacheName(cacheName), MAX_BYTES_BY_CACHE_NAME[cacheName]);
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(err);
  }
}

export async function cleanupAllSlots() {
  if (!cacheApi) return;

  try {
    const names = await cacheApi.keys();

    for (const cacheName of CLEARABLE_CACHE_NAMES) {
      // `<base>` for slot 1, `<base>_<slot>` for the rest — the shape `getCacheName` writes
      const slotNames = names.filter((name) => name === cacheName || name.startsWith(`${cacheName}_`));

      for (const slotName of slotNames) {
        await cleanupCache(slotName, MAX_BYTES_BY_CACHE_NAME[cacheName]);
      }
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(err);
  }
}

async function cleanupCache(physicalCacheName: string, maxBytes?: number) {
  const cache = await cacheApi.open(physicalCacheName);
  const keys = await cache.keys();
  const now = Date.now();
  const survivors: CacheEntry[] = [];

  let index = 0;
  for (const request of keys) {
    if (index++ % CLEANUP_YIELD_BATCH === 0) await yieldToMain();
    const response = await cache.match(request);
    if (!response) continue;

    const lastAccess = Number(response.headers.get(LAST_ACCESS_HEADER));
    if (lastAccess && now - lastAccess > CACHE_TTL) {
      await cache.delete(request);
      continue;
    }

    survivors.push({ request, lastAccess, size: Number(response.headers.get(SIZE_HEADER)) });
  }

  await enforceSizeBudget(cache, survivors, maxBytes);
}

// The TTL above bounds how OLD cached media gets, not how MUCH of it there is — five days of an
// active account measured 1.2 GB. That matters beyond disk use: once the origin exceeds its storage
// quota (a share of free disk, so a near-full machine gets very little) the browser evicts its
// buckets wholesale, which takes unrelated caches down with it.
async function enforceSizeBudget(cache: Cache, entries: CacheEntry[], maxBytes?: number) {
  if (!maxBytes || !entries.length) return;

  const unsized = entries.filter((entry) => !entry.size);
  if (unsized.length) {
    const averageSize = await estimateAverageSize(cache, unsized);
    unsized.forEach((entry) => {
      entry.size = averageSize;
    });
  }

  let total = entries.reduce((sum, entry) => sum + entry.size, 0);
  if (total <= maxBytes) return;

  // Least recently accessed first, so eviction follows the same signal the TTL does. Entries with no
  // recorded access time sort to the front — they predate the header and are the oldest by definition.
  entries.sort((a, b) => a.lastAccess - b.lastAccess);

  let index = 0;
  for (const entry of entries) {
    if (total <= maxBytes) break;

    if (index++ % CLEANUP_YIELD_BATCH === 0) await yieldToMain();
    await cache.delete(entry.request);
    total -= entry.size;
  }
}

// Entries saved before `SIZE_HEADER` existed carry no size. Measuring all of them would reintroduce
// the full-cache read this header exists to avoid, so measure an evenly spaced sample and apply its
// average to the rest — close enough to trim against, and it self-corrects as media is re-saved.
async function estimateAverageSize(cache: Cache, entries: CacheEntry[]) {
  const sampleCount = Math.min(SIZE_SAMPLE_LIMIT, entries.length);
  const step = Math.max(1, Math.floor(entries.length / sampleCount));

  let total = 0;
  let counted = 0;
  for (let i = 0; i < entries.length && counted < sampleCount; i += step) {
    await yieldToMain();
    const response = await cache.match(entries[i].request);
    if (!response) continue;

    try {
      total += (await response.blob()).size;
      counted++;
    } catch {
      // A partially written entry cannot be sized; skip it rather than abandoning the whole pass
    }
  }

  return counted ? total / counted : 0;
}

function getDataSize(data: Blob | ArrayBuffer | string) {
  if (data instanceof Blob) return data.size;
  if (data instanceof ArrayBuffer) return data.byteLength;
  return data.length;
}

// "Clear media cache" has to remove more than the hourly cleanup maintains. Two media caches cannot
// take part in that pass and were therefore never cleared by it — nor by this button:
//   - the service-worker cache holds opaque responses, which expose no headers to age them by (it is
//     also the largest of them all: 174 MB of photos and stickers on the profile this was measured on)
//   - the frame cache is one blob under a fixed key, not a set of per-media entries
// Leaving them behind meant pressing the button freed a fraction of what the user was told it would.
// The service worker writes its media cache with no account suffix (it has no slot), so it needs the
// raw names rather than the slot-scoped one `clear` derives. The superseded name goes too, in case
// this profile never activated a worker that dropped it.
export function clearServiceWorkerMedia() {
  [MEDIA_CACHE_NAME_SW, ...LEGACY_MEDIA_CACHE_NAMES_SW].forEach((name) => {
    void cacheApi?.delete(name).catch(() => undefined);
  });
  // The in-RAM set is derived from that cache: leaving it populated makes every dropped photo render
  // as "already decoded", so it skips its thumbnail and spinner and shows an empty box until the
  // network answers.
  clearLoadedStaticProxyUrls();
}

export function purgeClearableCache() {
  CLEARABLE_CACHE_NAMES.forEach((cacheName) => clear(cacheName));
  clear(MEDIA_FRAME_CACHE_NAME);
  clearServiceWorkerMedia();
}

async function updateAccessTime(cache: Cache, request: Request, response: Response) {
  try {
    const headers = new Headers(response.headers);
    headers.set(LAST_ACCESS_HEADER, Date.now().toString());
    const newResponse = new Response(response.clone().body, {
      status: response.status,
      statusText: response.statusText,
      headers,
    });
    await cache.put(request, newResponse);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(err);
  }
}
